import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL = `${SITE.url}/press`;
const CITE_YEAR = 2026;

export const Route = createFileRoute("/press")({
  head: () => ({
    meta: [
      { title: `Press & expert sources — ${SITE.name}` },
      { name: "description", content: "Map Observer for journalists: brand assets, citation snippets, and editors available for comment on Google Business Profile, the Map Pack, and local search." },
      { property: "og:title", content: "Press & expert sources" },
      { property: "og:description", content: "Citation snippets, brand assets, and on-the-record sources for local search stories." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Page,
});

const CITE_APA = `Map Observer. (${CITE_YEAR}). Map Observer: Local search, Google Maps & Business Profile intelligence. https://mapobserver.com`;
const CITE_HTML = `<a href="https://mapobserver.com">Map Observer</a> — the authoritative newsroom on Google Business Profile and the Map Pack.`;

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">For journalists &amp; researchers</p>
        <h1 className="headline-display text-4xl mt-3">Press &amp; expert sources</h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Working on a story about local search, Google Business Profile, the Map Pack, or
          AI Overviews? Map Observer&rsquo;s editors are available as on-the-record sources,
          and our trackers are free to cite.
        </p>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Available to comment on</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <li className="border border-border p-4">Map Pack volatility and unannounced ranking shifts</li>
            <li className="border border-border p-4">Google Business Profile suspensions, reinstatement, and policy</li>
            <li className="border border-border p-4">AI Overviews, AI Mode, and Ask Maps&rsquo; impact on local discovery</li>
            <li className="border border-border p-4">Review and reputation policy, fake-review enforcement</li>
            <li className="border border-border p-4">Local Service Ads (LSAs) and Google&rsquo;s pay-to-play surfaces</li>
            <li className="border border-border p-4">Multi-location and franchise SEO operations</li>
          </ul>
          <p className="mt-4 text-sm">
            Reach the editorial desk at{" "}
            <a href="mailto:press@mapobserver.com" className="text-accent underline">press@mapobserver.com</a>.
            We typically reply within one business day.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Cite Map Observer</h2>
          <p className="mt-4 text-sm">Citation snippets, free to use:</p>

          <div className="mt-4 space-y-4">
            <div>
              <p className="eyebrow">APA</p>
              <pre className="mt-1 text-xs bg-paper-soft border border-border p-3 overflow-x-auto whitespace-pre-wrap">{CITE_APA}</pre>
            </div>
            <div>
              <p className="eyebrow">HTML link</p>
              <pre className="mt-1 text-xs bg-paper-soft border border-border p-3 overflow-x-auto whitespace-pre-wrap">{CITE_HTML}</pre>
            </div>
          </div>

          <p className="mt-6 text-sm">
            For per-article citations, every story has a built-in "Cite this" button that
            produces APA, MLA, Chicago, and BibTeX. Our trackers — the{" "}
            <Link to="/local-search-index" className="text-accent underline">Local Search Index</Link>,{" "}
            <Link to="/updates" className="text-accent underline">algorithm updates</Link>, and{" "}
            <Link to="/changelog" className="text-accent underline">product changelog</Link> — are
            updated continuously and free to reference.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Embed our data</h2>
          <p className="mt-4 text-sm">
            We publish free, do-follow-attributed widgets you can drop into a story or
            dashboard: live Map Pack volatility, the last confirmed algorithm update, and a
            free Business Profile health check. See the{" "}
            <Link to="/embeds" className="text-accent underline">embed library</Link>.
          </p>
          <p className="mt-3 text-sm">
            Need raw JSON for a story or dashboard? See the{" "}
            <a href="/api/v1" className="text-accent underline">public data API</a>.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Brand &amp; description</h2>
          <p className="mt-4 text-sm font-semibold">{SITE.name}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            The daily briefing on Google Business Profile, the Map Pack, and local search —
            read by local SEO agencies, GBP specialists, and the multi-location operators
            they serve.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
